import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState, AppActionCreators } from '../store';
import { refreshTokenShared } from '../services/RefreshCoordinator';

interface DecodedToken {
  exp: number;
  [key: string]: any;
}

export function decodeJwtPayload(token: string): DecodedToken | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
const INACTIVITY_WINDOW_MS = 2 * 60 * 60 * 1000;

export const useRefreshToken = () => {
  const dispatch = useDispatch();
  const token = useSelector((state: ApplicationState) => state.app?.token);
  const refreshTokenValue = useSelector((state: ApplicationState) => state.app?.refreshToken);

  const refreshTimerRef = useRef<any>(null);
  const expirationCheckIntervalRef = useRef<any>(null);
  const inactivityTimerRef = useRef<any>(null);
  const isActiveRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const hasSessionExpiredHandledRef = useRef(false);
  const lastActivityTimeRef = useRef<number>(Date.now());

  const handleSessionExpiration = useCallback(() => {
    if (hasSessionExpiredHandledRef.current) return;
    hasSessionExpiredHandledRef.current = true;

    (dispatch as any)(AppActionCreators.LogoutCredentials());
    window.location.href = '/auth/login?sessionExpired=true';
  }, [dispatch]);

  const isTokenExpired = useCallback((currentToken: string) => {
    const decoded = decodeJwtPayload(currentToken);

    if (!decoded) return true;
    return Date.now() >= decoded.exp * 1000;
  }, []);

  const performTokenRefresh = useCallback(async () => {
    if (!refreshTokenValue || isRefreshingRef.current) return;

    try {
      isRefreshingRef.current = true;
      const data = await refreshTokenShared(refreshTokenValue);

      if (data && data.success) {
        const newToken = data.tokenResponse.access_token;
        const newRefreshToken = data.tokenResponse.refresh_token;

        (dispatch as any)(AppActionCreators.SetToken(newToken));
        (dispatch as any)(AppActionCreators.SetRefreshToken(newRefreshToken));
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshTokenValue, dispatch]);

  const scheduleTokenRefresh = useCallback(
    (currentToken: string) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (expirationCheckIntervalRef.current) {
        clearInterval(expirationCheckIntervalRef.current);
        expirationCheckIntervalRef.current = null;
      }

      if (!currentToken) return;
      if (isTokenExpired(currentToken)) {
        handleSessionExpiration();
        return;
      }

      const decoded = decodeJwtPayload(currentToken);
      if (!decoded) return;

      const expirationTime = decoded.exp * 1000;
      const now = Date.now();
      const timeToExpiration = Math.max(0, expirationTime - now);
      const timeUntilRefresh = Math.max(0, timeToExpiration - REFRESH_THRESHOLD_MS);

      refreshTimerRef.current = setTimeout(() => {
        if (Date.now() >= expirationTime) {
          handleSessionExpiration();
          return;
        }
        if (isActiveRef.current) {
          performTokenRefresh();
        } else {
          handleSessionExpiration();
        }
      }, timeUntilRefresh);

      expirationCheckIntervalRef.current = setInterval(() => {
        if (Date.now() >= expirationTime) {
          handleSessionExpiration();
        }
      }, 60 * 1000);
    },
    [performTokenRefresh, handleSessionExpiration, isTokenExpired]
  );

  const resetUserActivity = useCallback(() => {
    isActiveRef.current = true;
    lastActivityTimeRef.current = Date.now();

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      isActiveRef.current = false;
      if (token) {
        handleSessionExpiration();
      }
    }, INACTIVITY_WINDOW_MS);
  }, [token, handleSessionExpiration]);

  useEffect(() => {
    resetUserActivity();

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetUserActivity);
    });

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (expirationCheckIntervalRef.current) clearInterval(expirationCheckIntervalRef.current);

      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetUserActivity);
      });
    };
  }, [resetUserActivity]);

  useEffect(() => {
    const checkOnResume = async () => {
      if (document.visibilityState === 'hidden') return;
      if (!token) return;

      const inactiveMs = Date.now() - lastActivityTimeRef.current;
      if (inactiveMs >= INACTIVITY_WINDOW_MS) {
        handleSessionExpiration();
        return;
      }
      resetUserActivity();

      if (!isTokenExpired(token)) return;

      if (refreshTokenValue && !isRefreshingRef.current) {
        try {
          await performTokenRefresh();
        } catch {
          handleSessionExpiration();
        }
      } else {
        handleSessionExpiration();
      }
    };

    window.addEventListener('focus', checkOnResume);
    document.addEventListener('visibilitychange', checkOnResume);

    return () => {
      window.removeEventListener('focus', checkOnResume);
      document.removeEventListener('visibilitychange', checkOnResume);
    };
  }, [
    token,
    refreshTokenValue,
    isTokenExpired,
    handleSessionExpiration,
    performTokenRefresh,
    resetUserActivity
  ]);

  useEffect(() => {
    if (token) {
      hasSessionExpiredHandledRef.current = false;
      scheduleTokenRefresh(token);
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (expirationCheckIntervalRef.current) clearInterval(expirationCheckIntervalRef.current);
    };
  }, [token, scheduleTokenRefresh]);

  return null;
};

export default useRefreshToken;
